import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Settings2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { getConsent, setConsent, DEFAULT_CONSENT } from "@/lib/consent";

const CATEGORIES = [
  {
    key: "necessary",
    title: "Cookies necesare",
    desc:
      "Strict necesare pentru funcționarea site-ului (formular contact, securitate). Nu pot fi dezactivate.",
    locked: true,
  },
  {
    key: "analytics",
    title: "Cookies de analiză",
    desc:
      "Ne ajută să înțelegem cum este folosit site-ul (ex: Google Analytics). Sunt anonime.",
    locked: false,
  },
  {
    key: "marketing",
    title: "Cookies de marketing",
    desc:
      "Folosite pentru a măsura performanța campaniilor și pentru remarketing (ex: Meta Pixel).",
    locked: false,
  },
];

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [openCustom, setOpenCustom] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_CONSENT);

  useEffect(() => {
    const c = getConsent();
    if (!c) {
      // Slight delay so it doesn't pop instantly on first paint
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    } else {
      setPrefs(c);
    }
    // Allow re-opening from footer link via custom event
    const onOpen = () => {
      const cur = getConsent() || DEFAULT_CONSENT;
      setPrefs(cur);
      setOpenCustom(true);
      setVisible(true);
    };
    window.addEventListener("c2-open-cookie-settings", onOpen);
    return () => window.removeEventListener("c2-open-cookie-settings", onOpen);
  }, []);

  const acceptAll = () => {
    setConsent({ necessary: true, analytics: true, marketing: true });
    setVisible(false);
    setOpenCustom(false);
  };

  const rejectAll = () => {
    setConsent({ necessary: true, analytics: false, marketing: false });
    setVisible(false);
    setOpenCustom(false);
  };

  const savePrefs = () => {
    setConsent({
      necessary: true,
      analytics: !!prefs.analytics,
      marketing: !!prefs.marketing,
    });
    setVisible(false);
    setOpenCustom(false);
  };

  if (!visible && !openCustom) return null;

  return (
    <>
      {/* Bottom banner */}
      {visible && !openCustom && (
        <div
          data-testid="cookie-banner"
          className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:max-w-md z-[80] animate-fadeUp"
        >
          <div className="rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/10 p-5">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-[#E91E8B]/10 text-magenta grid place-items-center flex-shrink-0">
                <Cookie size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-base font-bold text-slate-900">
                  Folosim cookies
                </h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                  Utilizăm cookies pentru a îmbunătăți experiența ta și pentru
                  a analiza traficul. Poți accepta, refuza sau personaliza.{" "}
                  <Link
                    to="/politica-cookies"
                    className="text-magenta font-semibold hover:underline"
                  >
                    Află mai multe
                  </Link>
                  .
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    data-testid="cookie-accept-all"
                    onClick={acceptAll}
                    className="rounded-full bg-magenta hover:bg-[#C2185B] text-white h-9 px-4 text-sm font-semibold"
                  >
                    Acceptă toate
                  </Button>
                  <Button
                    data-testid="cookie-reject-all"
                    onClick={rejectAll}
                    variant="outline"
                    className="rounded-full h-9 px-4 text-sm font-semibold border-slate-300 hover:text-magenta hover:border-magenta bg-white"
                  >
                    Refuză toate
                  </Button>
                  <Button
                    data-testid="cookie-customize"
                    onClick={() => setOpenCustom(true)}
                    variant="ghost"
                    className="rounded-full h-9 px-3 text-sm font-semibold text-slate-700 hover:text-magenta hover:bg-transparent"
                  >
                    <Settings2 size={14} className="mr-1.5" />
                    Personalizează
                  </Button>
                </div>
              </div>
              <button
                onClick={rejectAll}
                aria-label="Închide"
                className="text-slate-400 hover:text-slate-700 transition-colors"
                data-testid="cookie-close"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customize dialog */}
      <Dialog open={openCustom} onOpenChange={setOpenCustom}>
        <DialogContent
          data-testid="cookie-customize-dialog"
          className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <div className="h-10 w-10 rounded-xl bg-[#E91E8B]/10 text-magenta grid place-items-center mb-2">
              <ShieldCheck size={20} />
            </div>
            <DialogTitle className="font-display text-2xl">
              Setări cookies
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Alege ce categorii de cookies dorești să accepți. Poți schimba
              preferințele oricând.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 mt-2">
            {CATEGORIES.map((cat) => (
              <div
                key={cat.key}
                className="flex items-start justify-between gap-4 p-4 rounded-xl border border-slate-200 bg-slate-50/60"
              >
                <div>
                  <div className="font-semibold text-slate-900 text-sm">
                    {cat.title}
                  </div>
                  <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                    {cat.desc}
                  </p>
                </div>
                <Switch
                  data-testid={`cookie-switch-${cat.key}`}
                  checked={cat.locked ? true : !!prefs[cat.key]}
                  disabled={cat.locked}
                  onCheckedChange={(v) =>
                    setPrefs((p) => ({ ...p, [cat.key]: v }))
                  }
                  className="data-[state=checked]:bg-magenta"
                />
              </div>
            ))}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
            <Button
              data-testid="cookie-save-prefs"
              onClick={savePrefs}
              className="rounded-full bg-magenta hover:bg-[#C2185B] text-white"
            >
              Salvează preferințele
            </Button>
            <Button
              onClick={acceptAll}
              variant="outline"
              className="rounded-full border-slate-300 hover:text-magenta hover:border-magenta bg-white"
            >
              Acceptă toate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
